import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, Heading, Separator, ShimmerText, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import { EmailPreference } from '~/graphql/schema';

import { EmailPreferencesDocument } from './graphql/query.emailPreferences.generated';

import { ErrorBoundary } from '../../../components/error-boundary/ErrorBoundary';
import { SambaAutoLink } from '../../../components/SambaAutoLink';
import { urlBuilder } from '../../../utils/urlBuilder';

// using this border color instead of tokens to match the other cards on the page using rails style closet components
const scBorderColor = '#DBDBDB';

const cardHeaderAndFooterStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  p: 'space.300',
};

const boxTableRowStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  py: 'space.100',
  px: 'space.300',
};

type BoxTableRowProps = {
  label: string;
  isSettingActive: boolean;
};

// We include a single whitespace after the label just to generate better text for our
// test asserations. It has no impact on the actual display
const BoxTableRow: React.FC<BoxTableRowProps> = ({ label, isSettingActive }) => (
  <li className={css({ listStyleType: '', padding: 'space.0' })}>
    <Box css={boxTableRowStyles}>
      <Text>{`${label} `} </Text>
      <Text color={isSettingActive ? 'text.info' : 'text'}>{isSettingActive ? 'On' : 'Off'}</Text>
    </Box>
  </li>
);

const ShimmerLoader = () => {
  return (
    <Box
      aria-busy="true"
      aria-label="Loading..."
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'space.100',
        maxWidth: '100%',
      }}
      role="status"
    >
      {Array.from({ length: 3 }, (_, index) => (
        <Box css={boxTableRowStyles} key={index}>
          <ShimmerText css={{ maxWidth: '25%' }} textStyle="text50" />
          <ShimmerText css={{ maxWidth: '5%' }} textStyle="text50" />
        </Box>
      ))}
    </Box>
  );
};

const EmailPreferencesContent = ({ employerId }: { employerId: string }) => {
  const { data } = useGraphQLQuery(EmailPreferencesDocument, {
    variables: { employerId },
    throwOnError: true,
  });

  return (
    <Card.Content css={{ mb: 'space.800' }}>
      {data && (
        <ul aria-labelledby="listHeader" className={css({ listStyleType: '', padding: 'space.0' })}>
          {data.employer.emailPreferences.map((preference: EmailPreference) => (
            <BoxTableRow key={preference.category} label={preference.label} isSettingActive={preference.isEnabled} />
          ))}
        </ul>
      )}
      {!data && <ShimmerLoader />}
    </Card.Content>
  );
};

export const EmailPreferencesTable = ({ employerId }: { employerId: string }) => {
  return (
    <ErrorBoundary name="emailPreferences">
      <Card
        data-testid="email-preferences-card"
        css={{
          width: 'inherit',
          borderStyle: 'solid',
          borderWidth: 'thin',
          borderColor: scBorderColor,
          boxShadow: 'none',
        }}
      >
        <Card.Header css={cardHeaderAndFooterStyles}>
          <Heading id="listHeader" as="h3" variant="title200">
            Email preferences
          </Heading>
        </Card.Header>
        <EmailPreferencesContent employerId={employerId} />
        <Separator css={{ fullWidthWithInset: 'space.300' }} />
        <Card.Footer css={cardHeaderAndFooterStyles}>
          <SambaAutoLink to={urlBuilder.profileEmailPreferencesEdit()}>Edit your email preferences</SambaAutoLink>
        </Card.Footer>
      </Card>
    </ErrorBoundary>
  );
};
